import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import { API_DOMAIN } from '../../Reusable_Components/Config/apiConfig';
import katibuLogo from '../../../assets/katibu-logo.png';

/* ── Icons ────────────────────────────────────────────────────── */
const LockIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <rect x="3" y="7" width="10" height="7" rx="2"
      stroke="#3a4055" strokeWidth="1.4" />
    <path d="M5 7 V5 C5 3.3 6.3 2 8 2 C9.7 2 11 3.3 11 5 V7"
      stroke="#3a4055" strokeWidth="1.4" strokeLinecap="round" />
    <circle cx="8" cy="10.5" r="1" fill="#3a4055" />
  </svg>
);

const EyeIcon = ({ off }) => off ? (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M2 2 L14 14" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <path d="M6.5 6.6A3 3 0 0 0 9.4 9.5M4.2 4.3C2.9 5.2 2 6.5 2 8c0 0 2 4 6 4a6.3 6.3 0 0 0 3.8-1.3M6 3.1A6 6 0 0 1 14 8s-.6 1.4-1.8 2.5"
      stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
) : (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M2 8s2-4 6-4 6 4 6 4-2 4-6 4-6-4-6-4z"
      stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    <circle cx="8" cy="8" r="1.8" stroke="currentColor" strokeWidth="1.4" />
  </svg>
);

const BackIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M9 2 L4 7 L9 12"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CheckIcon = () => (
  <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
    <circle cx="24" cy="24" r="22" stroke="#22c55e" strokeWidth="2" />
    <path d="M14 24 L21 31 L34 17"
      stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const InvalidIcon = () => (
  <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
    <circle cx="24" cy="24" r="22" stroke="#ef4444" strokeWidth="2" />
    <path d="M16 16 L32 32M32 16 L16 32"
      stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" />
  </svg>
);

/* ── Password strength ────────────────────────────────────────── */
const getStrength = (pw) => {
  if (!pw) return { score: 0, label: '', color: '' };
  let score = 0;
  if (pw.length >= 8)                   score++;
  if (pw.length >= 12)                  score++;
  if (/[A-Z]/.test(pw))                 score++;
  if (/[0-9]/.test(pw))                 score++;
  if (/[^A-Za-z0-9]/.test(pw))          score++;
  if (score <= 1) return { score, label: 'Weak',   color: '#ef4444' };
  if (score <= 2) return { score, label: 'Fair',   color: '#f59e0b' };
  if (score <= 3) return { score, label: 'Good',   color: '#3b82f6' };
  return             { score, label: 'Strong', color: '#22c55e' };
};

/* ── Main component ───────────────────────────────────────────── */
const ResetPasswordPage = () => {
  const [searchParams]            = useSearchParams();
  const token                     = searchParams.get('token') || '';

  const [password,  setPassword]  = useState('');
  const [confirm,   setConfirm]   = useState('');
  const [showPw,    setShowPw]    = useState(false);
  const [showCf,    setShowCf]    = useState(false);
  const [loading,   setLoading]   = useState(false);
  const [success,   setSuccess]   = useState(false);
  const [error,     setError]     = useState('');

  const strength  = getStrength(password);
  const mismatch  = confirm.length > 0 && confirm !== password;
  const canSubmit = password.length >= 8 && password === confirm && !loading;

  useEffect(() => {
    if (!token) setError('This reset link is missing or invalid. Please request a new one.');
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError('');
    try {
      setLoading(true);
      await axios.post(`${API_DOMAIN}security/audit/password-reset/confirm/`, {
        token,
        new_password: password,
      });
      setSuccess(true);
    } catch (err) {
      const msg = err.response?.data?.detail;
      if (err.response?.status === 400 && msg) {
        setError(msg);
      } else {
        setError('Something went wrong. Please try again or request a new reset link.');
      }
    } finally {
      setLoading(false);
    }
  };

  /* ── No token state ───────────────────────────────────────── */
  const noToken = !token;

  return (
    <div className="login-page">
      <div className={`login-top-loader${loading ? ' login-top-loader--active' : ''}`} />

      {/* Logo */}
      <div className="login-logo">
        <img src={katibuLogo} alt="Katibu — Limitless Growth" className="login-logo-img" />
      </div>

      {/* Card */}
      <div className="login-card">

        {/* ── Success ─────────────────────────────────────── */}
        {success && (
          <div className="fp-success">
            <div className="fp-success-icon"><CheckIcon /></div>
            <h2 className="fp-success-title">Password Updated</h2>
            <p className="fp-success-body">
              Your password has been changed successfully. You can now log in
              with your new password.
            </p>
            <Link to="/login" className="fp-back-btn">
              <BackIcon /> Go to Login
            </Link>
          </div>
        )}

        {/* ── Invalid / missing token ──────────────────────── */}
        {!success && noToken && (
          <div className="fp-success">
            <div className="fp-success-icon"><InvalidIcon /></div>
            <h2 className="fp-success-title" style={{ color: '#fc8181' }}>Invalid Link</h2>
            <p className="fp-success-body">
              This reset link is missing or malformed. Please request a fresh one.
            </p>
            <Link to="/forgot-password" className="fp-back-btn">
              <BackIcon /> Request New Link
            </Link>
          </div>
        )}

        {/* ── Form ────────────────────────────────────────── */}
        {!success && !noToken && (
          <>
            <div className="login-card-system-name">
              <span className="login-card-system-name-crm">Set New Password</span>
              <span className="login-card-system-name-mgmt">Secure Your Account</span>
            </div>
            <div className="login-card-divider" />
            <h1 className="login-card-title">Choose a Password</h1>
            <p className="login-card-subtitle">
              Must be at least 8 characters. Use a mix of letters, numbers and symbols for a stronger password.
            </p>

            {error && (
              <div className="login-error">
                {error}&nbsp;
                {error.toLowerCase().includes('expired') && (
                  <Link to="/forgot-password" className="rp-error-link">Request a new link →</Link>
                )}
              </div>
            )}

            <form className="login-fields" onSubmit={handleSubmit}>

              {/* New password */}
              <div className="login-field-group">
                <label className="login-field-label">New Password</label>
                <div className="login-input-wrap">
                  <span className="login-input-icon"><LockIcon /></span>
                  <input
                    type={showPw ? 'text' : 'password'}
                    className="login-input rp-input-padded"
                    placeholder="Enter new password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    autoFocus
                  />
                  <button
                    type="button"
                    className="rp-eye-btn"
                    onClick={() => setShowPw(v => !v)}
                    tabIndex={-1}
                    aria-label={showPw ? 'Hide password' : 'Show password'}
                  >
                    <EyeIcon off={showPw} />
                  </button>
                </div>

                {/* Strength bar */}
                {password.length > 0 && (
                  <div className="rp-strength">
                    <div className="rp-strength-bar">
                      {[1,2,3,4].map(i => (
                        <div
                          key={i}
                          className="rp-strength-seg"
                          style={{
                            background: i <= Math.ceil(strength.score * 4 / 5)
                              ? strength.color
                              : 'rgba(255,255,255,0.06)',
                          }}
                        />
                      ))}
                    </div>
                    <span className="rp-strength-label" style={{ color: strength.color }}>
                      {strength.label}
                    </span>
                  </div>
                )}
              </div>

              {/* Confirm password */}
              <div className="login-field-group">
                <label className="login-field-label">Confirm Password</label>
                <div className="login-input-wrap">
                  <span className="login-input-icon"><LockIcon /></span>
                  <input
                    type={showCf ? 'text' : 'password'}
                    className={`login-input rp-input-padded${mismatch ? ' rp-input-error' : ''}`}
                    placeholder="Re-enter new password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="rp-eye-btn"
                    onClick={() => setShowCf(v => !v)}
                    tabIndex={-1}
                    aria-label={showCf ? 'Hide password' : 'Show password'}
                  >
                    <EyeIcon off={showCf} />
                  </button>
                </div>
                {mismatch && (
                  <p className="rp-mismatch">Passwords do not match</p>
                )}
              </div>

              <button type="submit" className="login-btn" disabled={!canSubmit}>
                {loading ? (
                  <>
                    <div className="login-spinner" />
                    Updating Password…
                  </>
                ) : (
                  <>
                    <span className="login-btn-arrow">→</span>
                    Set New Password
                  </>
                )}
              </button>
            </form>

            <div className="fp-back-row" style={{ marginTop: '18px' }}>
              <Link to="/login" className="fp-back-link">
                <BackIcon /> Back to Login
              </Link>
            </div>

            {/* Footer */}
            <div className="login-card-footer">
              <img src={katibuLogo} alt="Katibu" className="login-footer-logo" />
              <span className="login-footer-text">
                ERP v3.1.2 &nbsp;•&nbsp; Secure Session Lock &nbsp;•&nbsp; Licensed to Main Branch
              </span>
            </div>
          </>
        )}

      </div>
    </div>
  );
};

export default ResetPasswordPage;
